import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import apiClient from '../../api/apiClient';
import { useToast } from '../../context/ToastContext';

import ModelSpecification from '../../components/credit/scoring/ModelSpecification';
import GeneralTerms from '../../components/credit/scoring/GeneralTerms';
import CategoryCard from '../../components/credit/scoring/CategoryCard';
import WeightSummary from '../../components/credit/scoring/WeightSummary';
import ScoreMapping from '../../components/credit/scoring/ScoreMapping';
import ActionsSidebar from '../../components/credit/scoring/ActionsSidebar';

const CATEGORY_LABELS: Record<string, string> = {
  character: 'Character (Karakter)',
  capacity: 'Capacity (Kapasitas)',
  capital: 'Capital (Modal)',
  collateral: 'Collateral (Jaminan)',
  condition: 'Condition (Kondisi)',
};

const initialFormData = {
  title: '',
  status: 'draft',
  credit_type: '',
  debtor_type: '',
  is_entrepreneur: false,
  repayment_method: 'flat',
  repayment_capacity_percentage: 70,
  interest_type: 'flat',
  interest_rate: 12,
  admin_fee_percentage: 1,
  provision_percentage: 1,
  stamp_duty_fee: 10000,
  notary_fee: 0,
  is_default: false,
};

const initialCategoriesConfig = {
  character: { enabled: true, weight: 20 },
  capacity: { enabled: true, weight: 25 },
  capital: { enabled: true, weight: 20 },
  collateral: { enabled: true, weight: 20 },
  condition: { enabled: true, weight: 15 },
};

const initialScoreMapping = {
  A: { min: 85, max: 100, label: 'Sangat Layak', recommendation: 'approve' },
  B: { min: 70, max: 84, label: 'Layak', recommendation: 'approve' },
  C: { min: 50, max: 69, label: 'Perlu Pertimbangan', recommendation: 'review' },
  D: { min: 0, max: 49, label: 'Tidak Layak', recommendation: 'reject' },
};

export default function ScoringModelForm() {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const urlParams = new URLSearchParams(window.location.search);
  const modelId = urlParams.get('id');
  const isEdit = !!modelId;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [categoriesConfig, setCategoriesConfig] = useState(initialCategoriesConfig);
  const [indicators, setIndicators] = useState<any[]>([]);
  const [scoreMapping, setScoreMapping] = useState(initialScoreMapping);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadModel = useCallback(async () => {
    setLoading(true);
    try {
      const model = await apiClient.get(`/credit/scoring/${modelId}`);
      if (model) {
        setFormData({
          title: model.title || '',
          status: model.status || 'draft',
          credit_type: model.credit_type || '',
          debtor_type: model.debtor_type || '',
          is_entrepreneur: model.is_entrepreneur || false,
          repayment_method: model.repayment_method || 'flat',
          repayment_capacity_percentage: model.repayment_capacity_percentage || 70,
          interest_type: model.interest_type || 'flat',
          interest_rate: model.interest_rate || 12,
          admin_fee_percentage: model.admin_fee_percentage || 1,
          provision_percentage: model.provision_percentage || 1,
          stamp_duty_fee: model.stamp_duty_fee || 0,
          notary_fee: model.notary_fee || 0,
          is_default: model.is_default || false,
        });
        setCategoriesConfig(model.categories_config || initialCategoriesConfig);
        setIndicators(model.indicators || []);
        setScoreMapping(model.score_mapping || initialScoreMapping);
      }
    } catch (error) {
      console.error('Error loading model:', error);
      showError('Gagal memuat data model scoring');
    } finally {
      setLoading(false);
    }
  }, [modelId, showError]);

  useEffect(() => {
    if (isEdit) {
      loadModel();
    }
  }, [isEdit, loadModel]);

  const handleChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user types
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Spesifikasi Model
    if (!formData.title || formData.title.trim() === '') {
      newErrors.title = 'Judul model harus diisi';
    }

    if (!formData.status) {
      newErrors.status = 'Status harus dipilih';
    }

    if (!formData.credit_type) {
      newErrors.credit_type = 'Jenis kredit harus dipilih';
    }

    if (!formData.debtor_type) {
      newErrors.debtor_type = 'Jenis debitur harus dipilih';
    }

    // Ketentuan Umum & Asumsi Kredit
    if (!formData.repayment_method) {
      newErrors.repayment_method = 'Metode angsuran harus dipilih';
    }

    if (!formData.repayment_capacity_percentage || formData.repayment_capacity_percentage <= 0) {
      newErrors.repayment_capacity_percentage = 'Repayment capacity harus diisi';
    }

    if (!formData.interest_type) {
      newErrors.interest_type = 'Jenis bunga harus dipilih';
    }

    if (!formData.interest_rate || formData.interest_rate <= 0) {
      newErrors.interest_rate = 'Suku bunga harus diisi';
    }

    if (
      formData.admin_fee_percentage === undefined ||
      formData.admin_fee_percentage === null ||
      formData.admin_fee_percentage < 0
    ) {
      newErrors.admin_fee_percentage = 'Biaya admin harus diisi';
    }

    if (
      formData.provision_percentage === undefined ||
      formData.provision_percentage === null ||
      formData.provision_percentage < 0
    ) {
      newErrors.provision_percentage = 'Provisi harus diisi';
    }

    if (
      formData.stamp_duty_fee !== undefined &&
      formData.stamp_duty_fee !== null &&
      formData.stamp_duty_fee < 0
    ) {
      newErrors.stamp_duty_fee = 'Biaya materai tidak boleh negatif';
    }

    if (
      formData.notary_fee !== undefined &&
      formData.notary_fee !== null &&
      formData.notary_fee < 0
    ) {
      newErrors.notary_fee = 'Biaya notaris tidak boleh negatif';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCategoryWeightChange = (category: string, weight: string) => {
    setCategoriesConfig(prev => ({
      ...prev,
      [category]: { ...prev[category as keyof typeof prev], weight: parseInt(weight) || 0 },
    }));
  };

  const handleCategoryToggle = (category: string, enabled: boolean) => {
    setCategoriesConfig(prev => ({
      ...prev,
      [category]: { ...prev[category as keyof typeof prev], enabled },
    }));
  };

  const addIndicator = (category: string) => {
    const newIndicator = {
      id: `ind_${Date.now()}`,
      category,
      name: '',
      description: '',
      input_type: 'kualitatif',
      min_score: 0,
      max_score: 100,
      weight: 0,
      is_required: true,
      options: [],
    };
    setIndicators([...indicators, newIndicator]);
  };

  const updateIndicator = (id: string, field: string, value: any) => {
    setIndicators(indicators.map(ind => (ind.id === id ? { ...ind, [field]: value } : ind)));
  };

  const deleteIndicator = (id: string) => {
    setIndicators(indicators.filter(ind => ind.id !== id));
  };

  const getCategoryIndicators = (category: string) => {
    return indicators.filter(ind => ind.category === category);
  };

  const getTotalWeight = () => {
    return Object.values(categoriesConfig)
      .filter(cat => cat.enabled)
      .reduce((sum, cat) => sum + cat.weight, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    const totalWeight = getTotalWeight();
    if (totalWeight !== 100) {
      alert(`Total bobot kategori harus 100%. Saat ini: ${totalWeight}%`);
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        categories_config: categoriesConfig,
        indicators,
        score_mapping: scoreMapping,
        last_updated: new Date().toISOString().split('T')[0],
      };

      if (isEdit) {
        await apiClient.put(`/credit/scoring/${modelId}`, dataToSave);
        showSuccess('Model scoring berhasil diperbarui');
      } else {
        await apiClient.post('/credit/scoring', dataToSave);
        showSuccess('Model scoring berhasil ditambahkan');
      }

      navigate('/credit/scoring');
    } catch (error: any) {
      console.error('Error saving model:', error);
      const errorMessage = error?.message || 'Terjadi kesalahan saat menyimpan model scoring';
      showError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const totalWeight = getTotalWeight();
  const isWeightValid = totalWeight === 100;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Model Scoring' : 'Tambah Model Scoring Baru'}
          </h1>
          <p className="text-gray-500 mt-1">Konfigurasi model penilaian kelayakan kredit</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* A. Spesifikasi Model */}
            <ModelSpecification formData={formData} handleChange={handleChange} errors={errors} />

            {/* B. Ketentuan Umum */}
            <GeneralTerms formData={formData} handleChange={handleChange} errors={errors} />

            {/* C. Struktur Model Scoring 5C */}
            <div className="bg-white border rounded-lg">
              <div className="p-4 border-b">
                <h3 className="text-lg font-semibold">Struktur Model Scoring 5C (Dinamis)</h3>
                <p className="text-sm text-gray-500">
                  Konfigurasi kategori dan indikator penilaian
                </p>
              </div>
              <div className="p-4 space-y-4">
                {Object.keys(categoriesConfig).map(category => (
                  <CategoryCard
                    key={category}
                    category={category}
                    label={CATEGORY_LABELS[category]}
                    config={categoriesConfig[category as keyof typeof categoriesConfig]}
                    indicators={getCategoryIndicators(category)}
                    onToggle={enabled => handleCategoryToggle(category, enabled)}
                    onWeightChange={weight => handleCategoryWeightChange(category, weight)}
                    onAddIndicator={() => addIndicator(category)}
                    onUpdateIndicator={updateIndicator}
                    onDeleteIndicator={deleteIndicator}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <WeightSummary totalWeight={totalWeight} isValid={isWeightValid} />
            <ScoreMapping scoreMapping={scoreMapping} onChange={setScoreMapping} />
            <ActionsSidebar
              isDefault={formData.is_default}
              onDefaultChange={checked => handleChange('is_default', checked)}
              saving={saving}
              isValid={isWeightValid}
              onCancel={() => navigate(-1)}
            />
          </div>
        </div>
      </form>
    </div>
  );
}
